import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import './AlpacaNakedCard.css';

/**
 * Trade Watcher → "Stocks · Alpaca": alerts for stock positions with no working
 * protective stop in the user's own Alpaca account. Opt-in (many investors hold
 * without stops on purpose). One line when all is well; opens for settings,
 * what needs a look and recent alerts. Hidden for people without access (Pro).
 * embedded: inside Trade Watcher → Connections, under Alpaca (no connect prompt).
 */
const KIND_TEXT = {
  naked: 'No protective stop',
  partial: 'Stop covers only part',
  day_only: 'Only a DAY stop (expires at the close)',
};
const EVENT_TEXT = {
  naked: 'Unprotected',
  partial: 'Partly protected',
  day_only: 'DAY stop only',
  protected: 'Protected again',
  closed: 'Position closed',
};

function ago(iso) {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}

const headers = () => ({ 'Content-Type': 'application/json', ...getBearerAuthHeaders() });

export default function AlpacaNakedCard({ onNavigate, embedded = false }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.alpacaAlerts, { headers: headers() });
      if (res.status === 401 || res.status === 403 || res.status === 404) { setHidden(true); return; }
      const d = await res.json();
      if (d.success) setData(d);
    } catch (_e) { /* the card just doesn't show */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const save = async (patch) => {
    setBusy('save');
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.alpacaAlertsSettings, { method: 'PUT', headers: headers(), body: JSON.stringify(patch) });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t save');
      setData((cur) => ({ ...cur, settings: d.settings }));
      if (patch.enabled) setOpen(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const checkNow = async () => {
    setBusy('check');
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.alpacaAlertsCheck, { method: 'POST', headers: headers() });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t check');
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  if (hidden || !data) return null;

  if (!data.connected) {
    if (embedded) return null;
    return (
      <div className="anc anc--prompt">
        <span><strong>Stocks · Alpaca</strong> Get alerted when a stock position has no protective stop.</span>
        {onNavigate ? <button type="button" className="anc-btn" onClick={() => onNavigate('client-reporting?connect=alpaca')}>Connect Alpaca</button> : null}
      </div>
    );
  }

  const s = data.settings || {};
  const current = data.current || [];
  const flagged = current.filter((c) => c.alerted || c.kind === 'day_only');
  const summary = !s.enabled ? 'Alerts off'
    : s.lastError ? s.lastError
      : flagged.length ? `${flagged.length} position${flagged.length === 1 ? '' : 's'} need${flagged.length === 1 ? 's' : ''} a look`
        : 'All positions protected';

  return (
    <section className={`anc${embedded ? ' anc--embedded' : ''}${flagged.length && s.enabled ? ' is-alert' : ''}`} aria-label="Stock position alerts (Alpaca)">
      <button type="button" className="anc-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="anc-title">
          <strong>{embedded ? 'Email alerts for unprotected stocks' : `Stocks · Alpaca${data.isPaper ? ' (paper)' : ''}`}</strong>
          <span className={`anc-pill ${!s.enabled ? 'is-off' : flagged.length ? 'is-alert' : s.lastError ? 'is-warn' : 'is-ok'}`}>{summary}</span>
        </span>
        <span className="anc-meta">
          {s.enabled && s.lastCheckedAt ? `Checked ${ago(s.lastCheckedAt)}` : s.enabled ? 'Checks every 2 min in market hours' : ''}
          <span className="anc-chev" aria-hidden="true">{open ? '▴' : '▾'}</span>
        </span>
      </button>

      {open ? (
        <div className="anc-body">
          {error ? <p className="anc-error" role="alert">{error}</p> : null}
          <div className="anc-settings">
            <label className="anc-toggle">
              <input type="checkbox" checked={Boolean(s.enabled)} disabled={busy === 'save'} onChange={(e) => save({ enabled: e.target.checked })} />
              <span>Alert me when a stock position has no protective stop</span>
            </label>
            {s.enabled ? (
              <>
                <label className="anc-toggle">
                  <input type="checkbox" checked={Boolean(s.shortOnly)} disabled={busy === 'save'} onChange={(e) => save({ shortOnly: e.target.checked })} />
                  <span>Short positions only</span>
                </label>
                <label className="anc-toggle">
                  <input type="checkbox" checked={s.dayStopWarning !== false} disabled={busy === 'save'} onChange={(e) => save({ dayStopWarning: e.target.checked })} />
                  <span>Warn before the close when only a DAY stop protects a position</span>
                </label>
                <label className="anc-field">
                  <span>Ignore positions under</span>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    defaultValue={s.minValue || 0}
                    onBlur={(e) => { const v = Number(e.target.value) || 0; if (v !== (s.minValue || 0)) save({ minValue: v }); }}
                    aria-label="Ignore positions worth less than this many dollars"
                  />
                  <span>$</span>
                </label>
                <button type="button" className="anc-btn" onClick={checkNow} disabled={busy === 'check'}>{busy === 'check' ? 'Checking…' : 'Check now'}</button>
              </>
            ) : null}
          </div>
          <p className="anc-note">
            Checks your positions and open orders every 2 minutes while the market is open. A position counts as protected when a working stop, stop-limit or trailing stop
            covers the whole position (bracket and OCO stops count). Emails go out once a position is still unprotected on a second check. RiskLo only reads your account; it never places or cancels orders.
          </p>

          {s.enabled ? (
            <>
              <h4 className="anc-h">Right now</h4>
              {current.length ? (
                <ul className="anc-list">
                  {current.map((c) => (
                    <li key={c.symbol} className={`is-${c.kind}`}>
                      <strong>{c.symbol}</strong>
                      <span>{c.side} {c.qty}{c.kind === 'partial' ? ` · stop covers ${c.protectedQty}` : ''}</span>
                      <em>{KIND_TEXT[c.kind] || c.kind}{c.alerted ? '' : ' · confirming…'}</em>
                      <time>since {ago(c.since).replace(' ago', '')}</time>
                    </li>
                  ))}
                </ul>
              ) : <p className="anc-ok">Every position is protected.</p>}

              <h4 className="anc-h">Recent alerts</h4>
              {(data.events || []).length ? (
                <ul className="anc-list anc-list--events">
                  {data.events.slice(0, 12).map((e, i) => (
                    <li key={`${e.at}-${i}`} className={`is-${e.event}`}>
                      <strong>{e.symbol}</strong>
                      <span>{EVENT_TEXT[e.event] || e.event}{e.detail ? ` · ${e.detail}` : ''}</span>
                      <time>{ago(e.at)}</time>
                    </li>
                  ))}
                </ul>
              ) : <p className="anc-note">No alerts yet.</p>}
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
